import { QueryableFieldsQuery } from '../../../../types/generated-types';
import {
  ITieredEntitySelectionCriterion,
  EntityValueType,
  EntityComparator,
  IEntityCriteriaSimpleOperator,
  IEntityCriteriaCompoundOperator,
  EntityCriteriaOperator,
} from '../../../../common/types/shared/entitySelection';

// Map of value types to their display units
const valueTypeUnits: Record<EntityValueType, string> = {
  [EntityValueType.NUMBER]: '',
  [EntityValueType.PERCENTAGE]: '%',
  [EntityValueType.TEMPERATURE]: '°F',
  [EntityValueType.HUMIDITY]: '%',
  [EntityValueType.PRESSURE]: 'psi',
  [EntityValueType.VOLTAGE]: 'V',
  [EntityValueType.CURRENT]: 'A',
  [EntityValueType.POWER]: 'W',
  [EntityValueType.ENERGY]: 'kWh',
  [EntityValueType.FLOW_RATE]: 'gpm',
  [EntityValueType.CONCENTRATION]: 'ppm',
  [EntityValueType.LEVEL]: '%',
  [EntityValueType.SPEED]: 'mph',
  [EntityValueType.FREQUENCY]: 'Hz',
  [EntityValueType.BOOLEAN]: '',
  [EntityValueType.STRING]: '',
  [EntityValueType.DATE]: '',
  [EntityValueType.DATETIME]: '',
  [EntityValueType.ENUM]: '',
  [EntityValueType.PHYSICAL_ENUM]: '',
  [EntityValueType.ARRAY]: '',
  [EntityValueType.COORDINATES]: '',
  [EntityValueType.DURATION]: 'min',
  [EntityValueType.AIRFLOW]: 'cfm',
  [EntityValueType.STRING_ARRAY]: '',
  [EntityValueType.NUMBER_ARRAY]: '',
  [EntityValueType.BOOLEAN_ARRAY]: '',
  [EntityValueType.DATE_ARRAY]: '',
  [EntityValueType.LATITUDE]: '°',
  [EntityValueType.LONGITUDE]: '°',
};

const formatSingleValue = (value: any, valueType: EntityValueType): string => {
  if (value === undefined || value === null) return '';

  switch (valueType) {
    case EntityValueType.NUMBER:
    case EntityValueType.PERCENTAGE:
    case EntityValueType.TEMPERATURE:
    case EntityValueType.HUMIDITY:
    case EntityValueType.PRESSURE:
    case EntityValueType.VOLTAGE:
    case EntityValueType.CURRENT:
    case EntityValueType.POWER:
    case EntityValueType.ENERGY:
    case EntityValueType.FLOW_RATE:
    case EntityValueType.CONCENTRATION:
    case EntityValueType.LEVEL:
    case EntityValueType.SPEED:
    case EntityValueType.FREQUENCY:
    case EntityValueType.DURATION:
    case EntityValueType.AIRFLOW:
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) {
        return String(value);
      }
      return `${num}${valueTypeUnits[valueType]}`;

    case EntityValueType.BOOLEAN:
      return value ? 'true' : 'false';

    case EntityValueType.DATE:
    case EntityValueType.DATETIME:
      return new Date(value).toLocaleString();

    case EntityValueType.COORDINATES:
    case EntityValueType.LATITUDE:
    case EntityValueType.LONGITUDE:
      const coord = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(coord)) {
        return String(value);
      }
      return `${coord}${valueTypeUnits[valueType]}`;

    case EntityValueType.ARRAY:
    case EntityValueType.STRING_ARRAY:
    case EntityValueType.NUMBER_ARRAY:
    case EntityValueType.BOOLEAN_ARRAY:
    case EntityValueType.DATE_ARRAY:
      if (Array.isArray(value)) {
        return value.map((v) => formatSingleValue(v, valueType)).join(', ');
      }
      return String(value);

    case EntityValueType.ENUM:
    case EntityValueType.PHYSICAL_ENUM:
    case EntityValueType.STRING:
    default:
      return String(value);
  }
};

// Helper to ensure valueType is always EntityValueType
const getEnumValueType = (vt: any): EntityValueType => {
  if (typeof vt === 'string') {
    return (EntityValueType as any)[vt] ?? EntityValueType.STRING;
  }
  return vt;
};

export const formatCriterionValue = (
  criterion: ITieredEntitySelectionCriterion,
  fieldsData: QueryableFieldsQuery['queryableFields'],
): string => {
  const { values, valueType, comparator } = criterion;

  // Handle null checks
  if (
    comparator === EntityComparator.IS_NULL ||
    comparator === EntityComparator.IS_NOT_NULL
  ) {
    return '';
  }

  // Handle array operations
  if (
    comparator === EntityComparator.IN ||
    comparator === EntityComparator.NOT_IN ||
    comparator === EntityComparator.ARRAY_CONTAINS ||
    comparator === EntityComparator.ARRAY_NOT_CONTAINS ||
    comparator === EntityComparator.ARRAY_CONTAINS_ALL ||
    comparator === EntityComparator.CONTAINS_ALL ||
    comparator === EntityComparator.CONTAINS_ANY
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return values.map((v) => formatSingleValue(v, safeValueType)).join(', ');
  }

  // Handle range operations
  if (
    comparator === EntityComparator.IN_RANGE ||
    comparator === EntityComparator.NOT_IN_RANGE ||
    comparator === EntityComparator.BETWEEN ||
    comparator === EntityComparator.NOT_BETWEEN ||
    comparator === EntityComparator.WITHIN_RADIUS ||
    comparator === EntityComparator.WITHIN_BOUNDS
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return values.map((v) => formatSingleValue(v, safeValueType)).join(', ');
  }

  // Handle array size operations
  if (
    comparator === EntityComparator.ARRAY_SIZE ||
    comparator === EntityComparator.ARRAY_SIZE_GREATER_THAN ||
    comparator === EntityComparator.ARRAY_SIZE_LESS_THAN
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return `${values.length} items`;
  }

  // Handle geospatial operations
  if (
    comparator === EntityComparator.NEAR ||
    comparator === EntityComparator.WITHIN
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return values.map((v) => formatSingleValue(v, safeValueType)).join(', ');
  }

  // Handle existence and emptiness checks
  if (
    comparator === EntityComparator.EXISTS ||
    comparator === EntityComparator.NOT_EXISTS ||
    comparator === EntityComparator.EMPTY ||
    comparator === EntityComparator.NOT_EMPTY
  ) {
    return '';
  }

  // Handle pattern matching
  if (
    comparator === EntityComparator.REGEX ||
    comparator === EntityComparator.NOT_REGEX ||
    comparator === EntityComparator.TEXT_MATCHES
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return `/${values.join('/')}/`;
  }

  // Handle text operations
  if (
    comparator === EntityComparator.CONTAINS ||
    comparator === EntityComparator.NOT_CONTAINS ||
    comparator === EntityComparator.STARTS_WITH ||
    comparator === EntityComparator.ENDS_WITH ||
    comparator === EntityComparator.TEXT_CONTAINS ||
    comparator === EntityComparator.TEXT_STARTS_WITH ||
    comparator === EntityComparator.TEXT_ENDS_WITH
  ) {
    if (!values || values.length === 0) return '';
    const safeValueType = getEnumValueType(valueType);
    return `"${values.join('" "')}"`;
  }

  // Handle single value for all other cases
  const safeValueType = getEnumValueType(valueType);
  return formatSingleValue(values?.[0] ?? '', safeValueType);
};

// Helper to get operator symbol
const getOperatorSymbol = (operator: string): string => {
  switch (operator) {
    case EntityComparator.EQUAL_TO:
      return '=';
    case EntityComparator.NOT_EQUAL_TO:
      return '≠';
    case EntityComparator.GREATER_THAN:
      return '>';
    case EntityComparator.LESS_THAN:
      return '<';
    case EntityComparator.GREATER_THAN_OR_EQUAL_TO:
      return '≥';
    case EntityComparator.LESS_THAN_OR_EQUAL_TO:
      return '≤';
    case EntityComparator.CONTAINS:
      return 'contains';
    case EntityComparator.NOT_CONTAINS:
      return 'does not contain';
    case EntityComparator.STARTS_WITH:
      return 'starts with';
    case EntityComparator.ENDS_WITH:
      return 'ends with';
    case EntityComparator.IN:
      return 'in';
    case EntityComparator.NOT_IN:
      return 'not in';
    case EntityComparator.IN_RANGE:
      return 'in range';
    case EntityComparator.NOT_IN_RANGE:
      return 'not in range';
    case EntityComparator.BETWEEN:
      return 'between';
    case EntityComparator.NOT_BETWEEN:
      return 'not between';
    case EntityComparator.TEXT_CONTAINS:
      return 'text contains';
    case EntityComparator.TEXT_STARTS_WITH:
      return 'text starts with';
    case EntityComparator.TEXT_ENDS_WITH:
      return 'text ends with';
    case EntityComparator.TEXT_MATCHES:
      return 'text matches';
    case EntityComparator.IS_NULL:
      return 'is null';
    case EntityComparator.IS_NOT_NULL:
      return 'is not null';
    case EntityComparator.ARRAY_CONTAINS:
      return 'array contains';
    case EntityComparator.ARRAY_NOT_CONTAINS:
      return 'array not contains';
    case EntityComparator.ARRAY_CONTAINS_ALL:
      return 'array contains all';
    case EntityComparator.ARRAY_SIZE:
      return 'array size';
    case EntityComparator.ARRAY_SIZE_GREATER_THAN:
      return 'array size greater than';
    case EntityComparator.ARRAY_SIZE_LESS_THAN:
      return 'array size less than';
    case EntityComparator.IN_COLLECTION:
      return 'in collection';
    case EntityComparator.NOT_IN_COLLECTION:
      return 'not in collection';
    case EntityComparator.CONTAINS_ALL:
      return 'contains all';
    case EntityComparator.CONTAINS_ANY:
      return 'contains any';
    case EntityComparator.NEAR:
      return 'near';
    case EntityComparator.WITHIN:
      return 'within';
    case EntityComparator.WITHIN_RADIUS:
      return 'within radius';
    case EntityComparator.WITHIN_BOUNDS:
      return 'within bounds';
    case EntityComparator.EXISTS:
      return 'exists';
    case EntityComparator.NOT_EXISTS:
      return 'does not exist';
    case EntityComparator.REGEX:
      return 'matches';
    case EntityComparator.NOT_REGEX:
      return 'does not match';
    case EntityComparator.ALL:
      return 'all';
    case EntityComparator.NONE:
      return 'none';
    case EntityComparator.SOME:
      return 'some';
    case EntityComparator.NOT_SOME:
      return 'not some';
    case EntityComparator.EMPTY:
      return 'is empty';
    case EntityComparator.NOT_EMPTY:
      return 'is not empty';
    default:
      return operator;
  }
};

// Helper to get field type from fieldsData
const getFieldType = (
  fieldsData: QueryableFieldsQuery['queryableFields'],
  path: string,
): string | null => {
  if (!fieldsData) return null;
  const pathParts = path.split('.');
  let currentFields = fieldsData;
  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    if (!currentFields[part]) return null;
    if (i === pathParts.length - 1) {
      const value = currentFields[part];
      if (Array.isArray(value)) {
        if (
          value.length === 1 &&
          Array.isArray(value[0]) &&
          value[0].every((v) => typeof v === 'string')
        ) {
          return 'ArrayOfEnum';
        }
        // Check if it's an array of a specific type
        if (value.length === 1 && typeof value[0] === 'string') {
          return `[${value[0]}]`;
        }
        return 'String';
      }
      return value;
    }
    currentFields = currentFields[part];
  }
  return null;
};

// Helper to get field display name
const getFieldDisplayName = (path: string): string => {
  if (!path) return '';
  // Split by dots and capitalize each segment
  return path
    .split('.')
    .map((segment) => {
      // Handle camelCase by inserting spaces before capital letters
      return segment
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    })
    .join(' > ');
};

export const formatCriterionDisplayString = (
  criterion: ITieredEntitySelectionCriterion,
  fieldsData: QueryableFieldsQuery['queryableFields'],
  level: number = 0,
): string => {
  const { selectionPath, comparator, values, invert } = criterion;
  if (!selectionPath) return '';

  const fieldType = getFieldType(fieldsData, selectionPath);
  if (!fieldType) return selectionPath;

  // Handle both single value and array of values
  const valueToFormat = values && values.length > 0 ? values : [''];

  // For null checks, don't require a value
  if (comparator === 'is null' || comparator === 'is not null') {
    const operatorSymbol = getOperatorSymbol(comparator);
    const displayString = `${getFieldDisplayName(selectionPath)} ${operatorSymbol}`;
    return invert ? `not (${displayString})` : displayString;
  }

  // For all other cases, require both comparator and value
  if (
    !comparator ||
    valueToFormat[0] === undefined ||
    valueToFormat[0] === ''
  ) {
    return getFieldDisplayName(selectionPath);
  }

  const formattedValue = formatCriterionValue(criterion, fieldsData);
  const operatorSymbol = getOperatorSymbol(comparator);
  const displayString = `${getFieldDisplayName(selectionPath)} ${operatorSymbol} ${formattedValue}`;
  return invert
    ? `${'  '.repeat(level)}not (${displayString})`
    : `${'  '.repeat(level)}${displayString}`;
};

export const formatSimpleOperatorDisplayString = (
  operator: IEntityCriteriaSimpleOperator,
  fieldsData: QueryableFieldsQuery['queryableFields'],
  level: number = 0,
): string => {
  const criteriaStrings = operator.criteria.map((criterion) =>
    formatCriterionDisplayString(criterion, fieldsData),
  );
  const operatorSymbol =
    operator.operator === EntityCriteriaOperator.AND ? 'AND' : 'OR';

  // Format with line breaks and indentation
  const combinedString = criteriaStrings.reduce((acc, curr, idx) => {
    if (idx === 0) return `${'  '.repeat(level + 1)}${curr}`;
    return `${acc}\n${'  '.repeat(level + 2)}${operatorSymbol.trim()} ${curr}`;
  }, '');

  return operator.invert
    ? `${level ? '  '.repeat(level) + 'not (\n' : 'not '}${combinedString}${level ? '  '.repeat(level) + '\n)' : ''}`
    : `${level ? '  '.repeat(level) + '(\n' : '(\n'}${combinedString}${level ? '\n' + '  '.repeat(level) + ')' : '\n)'}`;
};

export const formatCompoundOperatorDisplayString = (
  operator: IEntityCriteriaCompoundOperator,
  targetModel: string,
  fieldsData: QueryableFieldsQuery['queryableFields'],
  level: number = 0,
): string => {
  const { operator: op, criteria } = operator;
  const hasSimpleOperators = (criteria.simpleOperators?.length ?? 0) > 0;

  const formattedCriteria = (criteria.criteria ?? []).map(
    (criterion) => `${formatCriterionDisplayString(criterion, fieldsData)}`,
  );

  const formattedSimpleOperators = (criteria.simpleOperators ?? []).map(
    (simpleOperator) =>
      `${level ? '(\n' : ''}${formatSimpleOperatorDisplayString(
        simpleOperator,
        fieldsData,
        (criteria.criteria?.length ?? 0) > 0 ? level + 1 : level,
      )}${level ? '\n)' : ''}`,
  );

  const formattedCompoundOperators = (criteria.compoundOperators ?? []).map(
    (compoundOperator) =>
      `${'  '.repeat((criteria.criteria?.length ?? 0) > 0 || hasSimpleOperators ? level + 1 : level)}${level ? (compoundOperator.invert ? 'not ' : 'x') + '(\n ' + '  '.repeat((criteria.criteria?.length ?? 0) > 0 || hasSimpleOperators ? level + 2 : level + 1) : ''}${formatCompoundOperatorDisplayString(
        compoundOperator,
        targetModel,
        fieldsData,
        (criteria.criteria?.length ?? 0) > 0 || hasSimpleOperators
          ? level + 1
          : level,
      )}${level ? '\n' + '  '.repeat((criteria.criteria?.length ?? 0) > 0 || hasSimpleOperators ? level + 2 : level + 1) : ''}${level ? '  '.repeat((criteria.criteria?.length ?? 0) > 0 || hasSimpleOperators ? level + 2 : level + 1) + ')' : ''}`,
  );

  const formattedEntities = [
    ...formattedCriteria,
    ...formattedSimpleOperators,
    ...formattedCompoundOperators,
  ];

  let formattedString: string;

  switch (op) {
    case EntityCriteriaOperator.AND:
      formattedString = `${'  '.repeat(level)}${level ? '(\n ' : ''}${formattedEntities.join('\n' + '  '.repeat(level + 1) + 'AND ')}${level ? '\n' + '  '.repeat(level + 1) + ')' : ''}`;
      break;
    case EntityCriteriaOperator.OR:
      formattedString = `${'  '.repeat(level)}${level ? '(\n' : ''}${formattedEntities.join('\n' + '  '.repeat(level + 1) + 'OR ')}${level ? '\n' + '  '.repeat(level + 1) + ')' : ''}`;
      break;
    default:
      formattedString = formattedEntities.join('\n ');
  }

  return `${level ? '  '.repeat(level) + (operator.invert ? 'not ' : '') + '(' : operator.invert ? 'not (\n' + '  '.repeat(level + 1) : ''}${formattedString}${level ? '  '.repeat(level) + ')' : operator.invert ? '\n' + '  '.repeat(level) + ')' : ''}`;
};
